import type { COMPANY_TYPES } from 'definitions';

export type SignupDetails = {
  first: string;
  last: string;
  email: string;
  password: string;
  confirm: string;
  company: string;
};

export type SignupDetailsErrors = Partial<Record<keyof SignupDetails, string>>;

export type SignupAccountType = (typeof COMPANY_TYPES)[number];

export const EMPTY_SIGNUP_DETAILS: SignupDetails = {
  first: '',
  last: '',
  email: '',
  password: '',
  confirm: '',
  company: '',
};

export const PASSWORD_HINT = 'At least 12 characters, with upper and lower case, a number and a symbol.';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/**
 * The same rules signup enforces on the server (validate-password.ts), in the same
 * order. They are repeated here so a rejected password is caught on the form rather
 * than after the visitor has been walked through the account-type question, which
 * is where the request is actually sent. The server stays the authority: if the two
 * drift, the visitor is sent back to the form with the server's message.
 */
export const validatePasswordRules = (password: string): string | null => {
  if (password.length < 12) return 'Use at least 12 characters.';
  if (!/[a-z]/.test(password)) return 'Add a lowercase letter.';
  if (!/[A-Z]/.test(password)) return 'Add an uppercase letter.';
  if (!/[0-9]/.test(password)) return 'Add a number.';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return 'Add a symbol such as ! or @.';
  return null;
};

/** Everything wrong with the form at once, so one submit shows every problem. */
export const validateSignupDetails = (details: Readonly<SignupDetails>): SignupDetailsErrors => {
  const errors: SignupDetailsErrors = {};

  if (!details.first.trim()) errors.first = 'Enter your first name.';
  if (!details.last.trim()) errors.last = 'Enter your last name.';

  if (!details.email.trim()) errors.email = 'Enter your work email.';
  else if (!EMAIL_PATTERN.test(details.email.trim())) errors.email = 'That does not look like an email address.';

  const passwordProblem = validatePasswordRules(details.password);
  if (passwordProblem) errors.password = passwordProblem;

  if (details.confirm !== details.password) errors.confirm = 'The passwords do not match.';
  if (!details.company.trim()) errors.company = 'Enter your company name.';

  return errors;
};
